import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Outlet, useLocation, Navigate } from 'react-router-dom';
import { Coffee, CreditCard } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useShop } from '../../context/ShopContext';
import { useBilling } from '../../context/BillingContext';
import { emitSubscriptionRequired } from '../../utils/subscriptionEvents';
import api from '../../config/api';

const TABS = ['overview','menu','tables','baristas','orders','posts','billing','analytics','profile','settings'];

const DashboardLayout = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { shop } = useShop();
  const { userActivePlan, userPlanLoading } = useBilling();
  const [subscriptionBlocked, setSubscriptionBlocked] = useState(false);

  // Determine active tab from pathname
  const activeTab = (() => {
    const seg = location.pathname.split('/').filter(Boolean);
    const tab = seg[1]; // /dashboard/<tab>
    return TABS.includes(tab) ? tab : 'overview';
  })();

  // Support legacy ?tab= query and CustomEvent navigation
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const qpTab = params.get('tab');
      if (qpTab && TABS.includes(qpTab) && qpTab !== activeTab) {
        navigate(`/dashboard/${qpTab}`, { replace: true });
      }
    } catch {}
  }, [location.search, activeTab, navigate]);

  useEffect(() => {
    const handler = (e) => {
      const requested = e.detail?.tab;
      if (requested && TABS.includes(requested)) {
        navigate(`/dashboard/${requested}`);
      }
    };
    window.addEventListener('dashboard-switch-tab', handler);
    return () => window.removeEventListener('dashboard-switch-tab', handler);
  }, [navigate]);

  // Subscription status check once
  useEffect(() => {
    (async () => {
      try {
        const response = await api.get('/auth/subscription-status');
        const data = response.data;
        if (!data.hasActiveSubscription && data.status !== 'ADMIN' && data.status !== 'NOT_APPLICABLE') {
          emitSubscriptionRequired({ subscriptionStatus: data.status, expiryDate: data.expiryDate });
          setSubscriptionBlocked(true);
        } else {
          setSubscriptionBlocked(false);
        }
      } catch {}
    })();
  }, []);

  const getPlanDisplayText = useCallback((plan) => {
    if (!plan) return '';
    if ((plan.planCode && plan.planCode === 'TRIAL_FREE') || (plan.planName && plan.planName.toLowerCase().includes('trial'))) {
      if (plan.expiresAt) {
        try {
          const diff = Math.ceil((new Date(plan.expiresAt).getTime() - Date.now()) / (1000*60*60*24));
          if (diff > 0) return `${diff} ${t('dashboard.daysLeft', 'Days left')}`;
          if (diff === 0) return t('dashboard.expiresToday', 'Expires today');
          return t('subscription.expired.now','Expired');
        } catch {}
      }
    }
    if (plan.billingInterval) return plan.billingInterval.toLowerCase();
    return t('dashboard.active','Active');
  }, [t]);

  if (!shop) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827]" dir={isArabic ? 'rtl' : 'ltr'}>
      <header className="bg-[#1f2937]/60 backdrop-blur-sm border-b border-gray-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center py-4 ${isArabic ? 'flex-row-reverse justify-between' : 'justify-between'}`}>
            <div className="flex items-center space-x-3">
              <Coffee className="text-[#a15507] w-8 h-8" />
              <h1 className="text-2xl font-bold text-white">{shop?.name || t('dashboard.loading','Loading...')}</h1>
            </div>
            <div className={`flex items-center space-x-4 ${isArabic ? 'flex-row-reverse' : ''}`}>
              <span className="text-gray-300">{t('dashboard.welcome')}, {user?.firstName || t('dashboard.user','User')}</span>
              {userPlanLoading ? (
                <div className="flex items-center space-x-2 bg-amber-600/20 border border-amber-500/30 rounded-lg px-3 py-1">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-500" />
                  <span className="text-amber-300 text-sm font-medium">{t('dashboard.loadingPlan','Loading plan...')}</span>
                </div>
              ) : userActivePlan ? (
                <div className="flex items-center space-x-2 bg-amber-600/20 border border-amber-500/30 rounded-lg px-3 py-1">
                  <CreditCard className="w-4 h-4 text-amber-500" />
                  <span className="text-amber-300 text-sm font-medium">{userActivePlan.planName || 'Free Trial'}</span>
                  <span className="text-amber-400 text-xs">({getPlanDisplayText(userActivePlan)})</span>
                </div>
              ) : null}
              <button onClick={logout} className="btn-primary">{t('dashboard.logout','Logout')}</button>
            </div>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className={`flex bg-[#1f2937]/50 rounded-lg p-1 mb-8 ${isArabic ? 'flex-row-reverse space-x-reverse' : 'space-x-1'}`}>
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => navigate(`/dashboard/${tab}`)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${activeTab === tab ? 'bg-[#a15507] hover:bg-[#924e06] text-white' : 'text-gray-300 hover:text-white hover:bg-[#1f2937]/50'}`}
            >
              {t(`dashboard.${tab}`)}
            </button>
          ))}
        </div>
        <Outlet context={{ subscriptionBlocked, setSubscriptionBlocked }} />
      </div>
    </div>
  );
};

export default DashboardLayout;
